import { calculatePlanPrice } from '@/apis/product';
import { createHomeProposal, retrieveHomeProposal } from '@/apis/proposal';
import { DeclarationItem } from '@/types/PlanSummary/DeclarationItem';
import { PlanSummaryState } from '@/types/PlanSummary/PlanSummaryState';
import {
  MyRumahQuotationData,
  QuotationData,
} from '@/types/PlanSummary/QuotationData';
import { HomeRetrieveProposalResponse } from '@/types/Proposal';
import { CalculatePlanPriceData } from '@/types/Proposal/CalculatePlanPriceData';
import { ApiResponseWrapper } from '@/types/Wrapper';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';

export const initialState: PlanSummaryState = {
  declaration: false,
  marketingConsent: false,
  payMode: '',
  quotationDetail: {
    ownership: 0,
    planType: 0,
    noOfStorey: 0,
    buildingSumInsured: 0,
    contentSumInsured: 0,
    unoccupied: false,
    lossOrDamage: false,
    startDate: '',
  },
  declarationList: [],
  addOn: [],
  planPrice: undefined,
  encodedOrderId: '',
  loading: false,
  error: false,
  readyForPayment: false,
};

const planSummarySlice = createSlice({
  name: 'planSummary',
  initialState,
  reducers: {
    resetPlanSummaryState: () => {
      return initialState;
    },
    updateQuotationDetail: (state, action: PayloadAction<QuotationData>) => {
      state.quotationDetail = action.payload;
    },
    updateMyRumahQuotationDetail: (
      state,
      action: PayloadAction<MyRumahQuotationData>,
    ) => {
      const data: QuotationData = {
        ...action.payload,
        noOfStorey: action.payload.noOfStorey ?? 0,
        planType: 1,
        buildingSumInsured: 0,
        contentSumInsured: 0,
        unoccupied: false,
        lossOrDamage: false,
      };
      state.quotationDetail = data;
    },
    updateAddOn: (state, action: PayloadAction<string>) => {
      if (state.addOn) {
        state.addOn.push(action.payload);
      } else {
        state.addOn = [action.payload];
      }
    },
    removeAddon: (state, action: PayloadAction<string>) => {
      if (state.addOn) {
        state.addOn = state.addOn.filter((element) => {
          return element !== action.payload;
        });
      } else {
        state.addOn = [];
      }
    },
    addDeclarationItem: (state, action: PayloadAction<DeclarationItem>) => {
      if (state.declarationList) {
        state.declarationList.push(action.payload);
      } else {
        state.declarationList = [action.payload];
      }
    },
    updateDeclarationItem: (
      state,
      action: PayloadAction<DeclarationItem[]>,
    ) => {
      state.declarationList = action.payload;
    },
    removeDeclarationItem: (state, action: PayloadAction<DeclarationItem>) => {
      state.declarationList = state.declarationList.filter(
        (item) =>
          item.declarationType !== action.payload.declarationType ||
          item.description !== action.payload.description ||
          item.value !== action.payload.value,
      );
    },
    updateDeclaration: (state, action: PayloadAction<boolean>) => {
      state.declaration = action.payload;
    },
    updateMarketingConsent: (state, action: PayloadAction<boolean>) => {
      state.marketingConsent = action.payload;
    },
    updatePayMode: (state, action: PayloadAction<string>) => {
      state.payMode = action.payload;
    },
    mapProposalDataToPlanSummary: (
      state,
      action: PayloadAction<HomeRetrieveProposalResponse>,
    ) => {
      const data = action.payload;
      state.readyForPayment = true;
      state.addOn = data.addOnList.flatMap((x) => x.code);
      state.declarationList = data.home.declaration;
      state.quotationDetail = {
        ownership: data.home.ownership,
        buildingType: data.home.buildingType,
        constructionType: data.home.constructionType,
        noOfStorey: data.home.noOfStorey,
        planType: data.home.planType,
        buildingSumInsured: data.home.buildingSumInsured,
        contentSumInsured: data.home.contentSumInsured,
        startDate: format(parseISO(data.startDate), 'yyyy-MM-dd'),
        unoccupied: false,
        lossOrDamage: false,
      };

      state.planPrice = {
        discount:
          data.discountAmount !== 0
            ? [
                {
                  discountAmount: data.discountAmount,
                  discountRate: data.discountRate,
                },
              ]
            : [],
        serviceTaxRate: data.taxRate,
        serviceTaxAmount: data.taxAmount,
        stampDutyAmount: data.stampDuty,
        planPremium: data.planPremium,
        grossPremium: data.premium,
        totalPremium: data.totalPremium,
        totalPremiumBeforeDiscount: data.totalPremiumBeforeDiscount,
        status: data.status,
        endDate: format(parseISO(data.endDate), 'dd/MM/yyyy'),
        addOn: [],
      };

      data.addOnList.forEach((x: any) => {
        const addOnData = {
          addOnId: x.addOnId,
          code: x.code,
          name: x.name,
          description: x.description,
          longDescription: '',
          addOnPremium: x.grossPremium,
          addOnRate: 0,
          isSelected: true,
        };
        state.planPrice?.addOn.push(addOnData);
      });
    },
  },
  extraReducers(builder) {
    builder
      .addCase(calculatePlanPrice.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(calculatePlanPrice.fulfilled, (state, action) => {
        state.loading = true;
        const planPriceResponse = action.payload.data as CalculatePlanPriceData;
        state.planPrice = planPriceResponse ?? state.planPrice;
        state.loading = false;
        state.error = false;
      })
      .addCase(calculatePlanPrice.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });

    builder.addCase(createHomeProposal.fulfilled, (state, action) => {
      state.encodedOrderId = action.payload.data;
      state.loading = false;
      state.error = false;
    });

    builder
      .addCase(retrieveHomeProposal.pending, (state) => {
        state.loading = true;
        state.proposalData = {
          message: '',
        } as ApiResponseWrapper<HomeRetrieveProposalResponse>;
      })
      .addCase(retrieveHomeProposal.fulfilled, (state, action) => {
        state.loading = false;
        const proposalData =
          action.payload as ApiResponseWrapper<HomeRetrieveProposalResponse>;
        state.proposalData = proposalData;

        if (proposalData.succeeded)
          state.encodedOrderId = proposalData.data.encodedOrderId;
      })
      .addCase(retrieveHomeProposal.rejected, (state, action) => {
        state.loading = false;
        state.proposalData =
          action.payload as ApiResponseWrapper<HomeRetrieveProposalResponse>;
      });
  },
});

export const {
  resetPlanSummaryState,
  updateQuotationDetail,
  updateMyRumahQuotationDetail,
  updateAddOn,
  removeAddon,
  addDeclarationItem,
  updateDeclarationItem,
  removeDeclarationItem,
  updateDeclaration,
  updateMarketingConsent,
  updatePayMode,
  mapProposalDataToPlanSummary,
} = planSummarySlice.actions;

export default planSummarySlice.reducer;
